import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { PERMISSIONS, ROLE_LABELS } from '../AdminApp';
import { Shield, Plus, Trash2, CheckCircle, User, Copy, Check } from 'lucide-react';

// Génère un code unique type SAFI-XXXX
function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'SAFI-';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function AdminRoles({ staff }) {
  const [staffList, setStaffList]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({ nom: '', role: 'trainer' });
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');
  const [error, setError]           = useState('');
  const [lastCode, setLastCode]     = useState('');
  const [copied, setCopied]         = useState(false);

  useEffect(() => { fetchStaff(); }, []);

  async function fetchStaff() {
    setLoading(true);
    const snap = await getDocs(collection(db, 'staff'));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    data.sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
    setStaffList(data);
    setLoading(false);
  }

  // IT crée une invitation — juste nom + rôle
  async function handleCreateInvitation() {
    setError(''); setMsg('');
    if (!form.nom || !form.role) { setError('Le nom et le rôle sont obligatoires.'); return; }
    setSaving(true);
    try {
      const code  = generateCode();
      const id    = `inv_${Date.now()}`;
      // Crée l'invitation dans Firestore
      await setDoc(doc(db, 'invitations', id), {
        nom:       form.nom,
        role:      form.role,
        roleLabel: ROLE_LABELS[form.role],
        code,
        used:      false,
        createdAt: serverTimestamp(),
        createdBy: staff?.uid,
      });
      // Crée aussi le document staff en attente
      await setDoc(doc(db, 'staff', id), {
        nom:       form.nom,
        role:      form.role,
        actif:     false, // sera activé quand le compte est créé
        invitationCode: code,
        createdAt: serverTimestamp(),
        createdBy: staff?.uid,
      });
      setLastCode(code);
      setMsg(`Invitation créée pour ${form.nom}.`);
      setForm({ nom: '', role: 'trainer' });
      setShowForm(false);
      await fetchStaff();
    } catch (err) {
      setError('Erreur lors de la création de l\'invitation.');
      console.error(err);
    }
    setSaving(false);
  }

  const copyCode = () => {
    navigator.clipboard.writeText(lastCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  async function handleChangeRole(member, newRole) {
    await updateDoc(doc(db, 'staff', member.id), { role: newRole });
    await fetchStaff();
  }

  async function handleDelete(member) {
    if (!window.confirm(`Supprimer le membre ${member.nom} ?`)) return;
    await deleteDoc(doc(db, 'staff', member.id));
    await fetchStaff();
  }

  async function handleToggleActif(member) {
    await updateDoc(doc(db, 'staff', member.id), { actif: !member.actif });
    await fetchStaff();
  }

  if (loading) return <div className="admin-page-loading"><div className="admin-spinner"/></div>;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h2>Rôles & Accès</h2>
          <p>Gérez les membres de l'équipe SAFI Academia</p>
        </div>
        <div className="admin-page__actions">
          <button className="admin-btn admin-btn--primary" onClick={() => { setShowForm(!showForm); setError(''); setMsg(''); setLastCode(''); }}>
            <Plus size={15}/> Inviter un membre
          </button>
        </div>
      </div>

      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}
      {error && <div className="admin-msg admin-msg--err">{error}</div>}

      {/* Code d'invitation généré */}
      {lastCode && (
        <div className="admin-invite-code">
          <div className="admin-invite-code__left">
            <Shield size={20}/>
            <div>
              <p>Code d'invitation généré</p>
              <span>Transmettez ce code au nouveau membre — il l'utilisera pour créer son compte.</span>
            </div>
          </div>
          <div className="admin-invite-code__right">
            <span className="admin-invite-code__code">{lastCode}</span>
            <button onClick={copyCode} className="admin-btn admin-btn--secondary">
              {copied ? <><Check size={14}/> Copié</> : <><Copy size={14}/> Copier</>}
            </button>
          </div>
        </div>
      )}

      {/* Formulaire invitation — juste nom + rôle */}
      {showForm && (
        <div className="admin-form-box">
          <h3><User size={16}/> Inviter un nouveau membre</h3>
          <p style={{fontSize:'0.85rem',color:'var(--admin-muted)',marginBottom:'1.25rem'}}>
            L'IT Manager entre uniquement le nom et le rôle. 
            Le membre recevra un code pour créer lui-même son compte.
          </p>
          <div className="admin-form-grid">
            <div className="admin-form-field">
              <label>Nom complet du membre</label>
              <div className="admin-input-icon">
                <User size={15}/>
                <input type="text" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Prénom Nom"/>
              </div>
            </div>
            <div className="admin-form-field">
              <label>Rôle</label>
              <select value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))}>
                {Object.entries(ROLE_LABELS).map(([k,v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Aperçu permissions */}
          <div className="admin-perm-preview">
            <p>Accès avec ce rôle :</p>
            <div className="admin-perm-preview__tags">
              {(PERMISSIONS[form.role]||[]).map(p => (
                <span key={p} className="admin-badge admin-badge--info">{p}</span>
              ))}
            </div>
          </div>

          <div style={{display:'flex',gap:'0.75rem',marginTop:'1.25rem'}}>
            <button className="admin-btn admin-btn--primary" onClick={handleCreateInvitation} disabled={saving}>
              {saving ? <span className="admin-spinner-sm"/> : 'Générer le code d\'invitation'}
            </button>
            <button className="admin-btn admin-btn--secondary" onClick={() => setShowForm(false)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Tableau des permissions */}
      <div className="admin-permissions-box">
        <h3><Shield size={16}/> Permissions par rôle</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Rôle</th>
              <th>Dashboard</th>
              <th>Étudiants</th>
              <th>Cours</th>
              <th>Finances</th>
              <th>Communication</th>
              <th>Rôles</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(PERMISSIONS).map(([role, perms]) => (
              <tr key={role}>
                <td><strong>{ROLE_LABELS[role]}</strong></td>
                {['dashboard','etudiants','cours','finances','communication','roles'].map(p => (
                  <td key={p} style={{textAlign:'center'}}>
                    {perms.includes(p)
                      ? <CheckCircle size={16} color="#2f9e44"/>
                      : <span style={{color:'var(--admin-border)'}}>—</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Liste des membres */}
      <div className="admin-table-box">
        <div className="admin-table-box__header">
          <h3>Membres de l'équipe ({staffList.length})</h3>
        </div>
        <table className="admin-table">
          <thead>
            <tr><th>Membre</th><th>Rôle</th><th>Statut</th><th>Compte</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {staffList.length === 0 && (
              <tr><td colSpan={5} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun membre</td></tr>
            )}
            {staffList.map(m => (
              <tr key={m.id}>
                <td>
                  <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                    <div className="admin-sidebar__avatar" style={{width:34,height:34,fontSize:'0.9rem',flexShrink:0}}>
                      {m.nom?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <strong>{m.nom}</strong>
                      <small>{m.email || 'Email non défini'}</small>
                    </div>
                  </div>
                </td>
                <td>
                  {m.id === staff?.uid ? (
                    <span className="admin-badge admin-badge--niveau">{ROLE_LABELS[m.role]}</span>
                  ) : (
                    <select value={m.role} onChange={e => handleChangeRole(m, e.target.value)} className="admin-select admin-select--sm">
                      {Object.entries(ROLE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  )}
                </td>
                <td>
                  <span className={`admin-badge ${m.actif ? 'admin-badge--ok' : 'admin-badge--warn'}`}>
                    {m.actif ? 'Actif' : 'En attente'}
                  </span>
                </td>
                <td>
                  {m.email ? (
                    <span style={{fontSize:'0.8rem',color:'var(--admin-muted)'}}>{m.email}</span>
                  ) : (
                    <span style={{fontSize:'0.78rem',color:'var(--admin-warn)',fontWeight:600}}>Compte non créé</span>
                  )}
                </td>
                <td>
                  {m.id !== staff?.uid && (
                    <div className="admin-table-actions">
                      <button
                        className={`admin-btn ${m.actif ? 'admin-btn--danger' : 'admin-btn--success'}`}
                        style={{padding:'0.3rem 0.7rem',fontSize:'0.78rem'}}
                        onClick={() => handleToggleActif(m)}
                      >
                        {m.actif ? 'Désactiver' : 'Activer'}
                      </button>
                      <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => handleDelete(m)}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  )}
                  {m.id === staff?.uid && <span style={{fontSize:'0.78rem',color:'var(--admin-muted)'}}>Vous</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}